// --- Core ---
import {ReactNode} from "react";
import {Field} from "react-final-form";
import {Collapse} from "@mui/material";

type FieldConditionMatcher<FieldValue, ExpectedValue = FieldValue> = (actual: FieldValue, expected: ExpectedValue) => boolean;

const presenceMatcher: FieldConditionMatcher<never> = (actual, _expected) => !!actual;
const identityMatcher: FieldConditionMatcher<never> = (actual, expected) => (actual === expected);

interface FieldConditionProps<FieldValue, ExpectedValue = FieldValue> {
  children: ReactNode;
  inverted?: boolean;
  // The expected value
  is: ExpectedValue;
  // Matcher to compare the actual and expected value. Default is identity (===)
  matcher?: FieldConditionMatcher<FieldValue, ExpectedValue>;
  name: string;
}

/**
 * Render children in a Collapse only when value of a field satisfies the condition.
 * IMPORTANT: Does not keep the children mounted
 */
const FieldConditionCollapse =
  <FieldValue, ExpectedValue = FieldValue>(
    {
      is,
      children,
      inverted = false,
      matcher = identityMatcher,
      ...rest
    }: FieldConditionProps<FieldValue, ExpectedValue>
  ) => (
    <Field<FieldValue> subscription={{value: true}} {...rest}>
      {
        ({input: {value}}) => {
          const isMatched = matcher(value, is);
          const showChildren = inverted ? !isMatched : isMatched;

          return <Collapse in={showChildren} unmountOnExit>{children}</Collapse>;
        }
      }
    </Field>
  );

/**
 * Render children only when value of a field satisfies the condition
 */
const FieldCondition =
  <FieldValue, ExpectedValue = FieldValue>(
    {
      is,
      children,
      inverted = false,
      matcher = identityMatcher,
      ...rest
    }: FieldConditionProps<FieldValue, ExpectedValue>
  ) => (
    <Field<FieldValue> subscription={{value: true}} {...rest}>
      {
        ({input: {value}}) => {
          const isMatched = matcher(value, is);
          const showChildren = inverted ? !isMatched : isMatched;

          return showChildren ? children : null;
        }
      }
    </Field>
  );

export {
  FieldCondition,
  FieldConditionCollapse,
  presenceMatcher
};
